"use client"

import * as React from "react"
import { debugLog } from '@/lib/debug'
import { Area, AreaChart, Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts"

import { getTokens } from "@/lib/auth"
import {
  getWorkspaces,
  getScenariosRange,
  getApplicationLaunchesRange,
  getActiveInstructionsRange,
  getActiveScenariosRange,
  getScenariosChangesRange,
  getCopilotRequestsRange,
  getActiveAiFilesRange,
} from "@/lib/api-v3"
import {
  Card,
  CardContent,
} from "@/components/ui/card"
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { BarChart3, TrendingUp } from "lucide-react"
import { Skeleton } from "@/components/ui/skeleton"
import { type AnalyticsPeriod, type CustomDateRange } from "@/components/analytics-period-selector"
import { getPeriodRange } from "@/lib/period-utils"

// Тип метрики для отображения
export type MetricType = 
  | 'scenarios' 
  | 'applications' 
  | 'activeInstructions' 
  | 'activeScenarios' 
  | 'scenariosChanges' 
  | 'copilotRequests' 
  | 'activeAiFiles'

// Конфигурация метрик
const METRICS_CONFIG: Record<MetricType, { label: string; color: string }> = {
  scenarios: { label: "Запуски процедур", color: "#50a3e5" },
  applications: { label: "Запуски приложения", color: "#dea8d2" },
  activeInstructions: { label: "Активные участки", color: "#82ca9d" },
  activeScenarios: { label: "Активные инструкции", color: "#ffc658" },
  scenariosChanges: { label: "Изменения процедур", color: "#ff7c7c" },
  copilotRequests: { label: "Запросы к Copilot", color: "#8884d8" },
  activeAiFiles: { label: "Активные AI-файлы", color: "#d084d8" },
}

type RangeChartItem = { date: string; value: number }

interface ChartAreaInteractiveProps {
  includeAdmins?: boolean
  period?: AnalyticsPeriod
  customRange?: CustomDateRange
  chartType?: 'area' | 'bar'
  metricType?: MetricType
  /** Если задан — не вызываем GET /workspaces, берём id из контекста страницы */
  workspaceId?: number | null
  onChartTypeChange?: (type: 'area' | 'bar') => void
  onMetricTypeChange?: (type: MetricType) => void
  /** Вызывается при любой ошибке загрузки данных */
  onError?: (error: string) => void
}

export function ChartAreaInteractive({ 
  includeAdmins = false, 
  period = 'month',
  customRange,
  chartType = 'area',
  metricType = 'scenarios',
  workspaceId: workspaceIdProp,
  onChartTypeChange,
  onMetricTypeChange,
  onError,
}: ChartAreaInteractiveProps) {
  const [accessToken, setAccessToken] = React.useState<string | null>(null)
  const [workspaceId, setWorkspaceId] = React.useState<number | null>(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const [rangeChartData, setRangeChartData] = React.useState<RangeChartItem[]>([])
  const [error, setError] = React.useState<string | null>(null)

  // Инициализация токена и workspace (из пропа или GET /workspaces)
  React.useEffect(() => {
    const init = async () => {
      try {
        const tokens = getTokens()
        if (!tokens?.accessToken) {
          setIsLoading(false)
          return
        }
        setAccessToken(tokens.accessToken)
        if (workspaceIdProp != null && workspaceIdProp !== undefined) {
          setWorkspaceId(workspaceIdProp)
          return
        }
        const workspaces = await getWorkspaces(tokens.accessToken)
        if (workspaces && workspaces.length > 0 && workspaces[0].workspaceId != null) {
          setWorkspaceId(workspaces[0].workspaceId)
        } else {
          setIsLoading(false)
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e)
        setError(msg)
        onError?.(msg)
        setIsLoading(false)
      }
    }
    init()
  }, [workspaceIdProp])

  // Загрузка данных при смене периода/группы/токена/типа метрики
  React.useEffect(() => {
    if (!accessToken || workspaceId == null) return
    const load = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const periodRange = getPeriodRange(period, customRange)
        
        debugLog('api', `[Chart] Период: ${period}, Метрика: ${metricType}`, {
          startDate: periodRange.startDate.toLocaleString('ru-RU'),
          endDate: periodRange.endDate.toLocaleString('ru-RU'),
          startTimestamp: periodRange.startTimestamp,
          endTimestamp: periodRange.endTimestamp,
          customRange
        })
        
        // Загружаем данные для выбранной метрики
        let metricData: number[] = []
        const params = {
          workspace: workspaceId,
          begin: periodRange.startTimestamp,
          end: periodRange.endTimestamp,
          ignoreAdmins: !includeAdmins,
        }

        switch (metricType) {
          case 'scenarios':
            metricData = await getScenariosRange(accessToken, params)
            break
          case 'applications':
            metricData = await getApplicationLaunchesRange(accessToken, params)
            break
          case 'activeInstructions':
            metricData = await getActiveInstructionsRange(accessToken, params)
            break
          case 'activeScenarios':
            metricData = await getActiveScenariosRange(accessToken, params)
            break
          case 'scenariosChanges':
            metricData = await getScenariosChangesRange(accessToken, params)
            break
          case 'copilotRequests':
            metricData = await getCopilotRequestsRange(accessToken, params)
            break
          case 'activeAiFiles':
            metricData = await getActiveAiFilesRange(accessToken, params)
            break
        }
        
        debugLog('api', `[Chart] API вернул данные для ${metricType}:`, {
          data: metricData, 
          length: metricData?.length,
        })
        
        const items = buildChartItems(
          periodRange.endTimestamp, 
          metricData
        )
        debugLog('api', `[Chart] Создано элементов графика:`, items.length, items)
        setRangeChartData(items)
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e)
        setError(msg)
        onError?.(msg)
        setRangeChartData([])
      } finally {
        setIsLoading(false)
      }
    }
    load()
  }, [accessToken, workspaceId, period, includeAdmins, customRange, metricType])

  // Получаем конфигурацию текущей метрики
  const currentMetricConfig = METRICS_CONFIG[metricType]
  const chartConfig: ChartConfig = {
    value: { 
      label: currentMetricConfig.label, 
      color: currentMetricConfig.color 
    }
  }

  // Функция для рендера графика
  const renderChart = () => {
    if (chartType === 'area') {
      return (
        <AreaChart 
          accessibilityLayer
          data={rangeChartData}
          margin={{
            left: -20,
            right: 12,
          }}
        >
          <defs>
            <linearGradient id="fillMetric" x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="5%"
                stopColor={currentMetricConfig.color}
                stopOpacity={0.8}
              />
              <stop
                offset="95%"
                stopColor={currentMetricConfig.color}
                stopOpacity={0.1}
              />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="date"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            minTickGap={32}
            tickFormatter={(value) => {
              const [yyyy, mm, dd] = String(value).split("-")
              return `${dd}.${mm}`
            }}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tickCount={3}
          />
          <ChartTooltip
            cursor={false}
            content={
              <ChartTooltipContent
                labelFormatter={(value) => formatRuFullDate(String(value))}
                indicator="dot"
              />
            }
          />
          <Area
            dataKey="value"
            fill="url(#fillMetric)"
            stroke={currentMetricConfig.color}
          />
        </AreaChart>
      )
    } else {
      return (
        <BarChart 
          accessibilityLayer
          data={rangeChartData}
          margin={{
            left: -20,
            right: 12,
          }}
        >
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="date"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            minTickGap={32}
            tickFormatter={(value) => {
              const [yyyy, mm, dd] = String(value).split("-")
              return `${dd}.${mm}`
            }}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tickCount={3}
          />
          <ChartTooltip
            cursor={false}
            content={
              <ChartTooltipContent
                labelFormatter={(value) => formatRuFullDate(String(value))}
              />
            }
          />
          <Bar
            dataKey="value"
            fill={currentMetricConfig.color}
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      )
    }
  }

  return (
    <Card className="@container/card rounded-none border-0 shadow-none py-0">
      <div className="flex items-center justify-between pb-2 gap-2">
        <Select value={metricType} onValueChange={(value: MetricType) => onMetricTypeChange?.(value)}>
          <SelectTrigger className="w-[240px]" size="sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="scenarios">
              {METRICS_CONFIG.scenarios.label}
            </SelectItem>
            <SelectItem value="applications">
              {METRICS_CONFIG.applications.label}
            </SelectItem>
            <SelectItem value="activeInstructions">
              {METRICS_CONFIG.activeInstructions.label}
            </SelectItem>
            <SelectItem value="activeScenarios">
              {METRICS_CONFIG.activeScenarios.label}
            </SelectItem>
            <SelectItem value="scenariosChanges">
              {METRICS_CONFIG.scenariosChanges.label}
            </SelectItem>
            <SelectItem value="copilotRequests">
              {METRICS_CONFIG.copilotRequests.label}
            </SelectItem>
            <SelectItem value="activeAiFiles">
              {METRICS_CONFIG.activeAiFiles.label}
            </SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={chartType} onValueChange={(value: 'area' | 'bar') => onChartTypeChange?.(value)}>
          <SelectTrigger className="w-[160px]" size="sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="area">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Линейная
              </div>
            </SelectItem>
            <SelectItem value="bar">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                Столбчатая
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      <CardContent className="px-0 pt-0 sm:px-0 sm:pt-0">
        {isLoading && (
          <Skeleton className="h-[250px] w-full" />
        )}
        {!isLoading && rangeChartData.length > 0 && (
          <ChartContainer config={chartConfig} className="aspect-auto h-[250px] w-full">
            {renderChart()}
          </ChartContainer>
        )}
        {!isLoading && rangeChartData.length === 0 && !error && (
          <div className="text-sm text-muted-foreground">Нет данных за выбранный период</div>
        )}
        {error && (
          <div className="text-sm text-destructive">Ошибка: {error}</div>
        )}
      </CardContent>
    </Card>
  )
}


function buildChartItems(
  endSec: number, 
  metricCounts: number[]
): RangeChartItem[] {
  // Берем количество дней из того, что пришло от API
  const daysToProcess = metricCounts?.length || 0
  
  debugLog('api', `[buildChartItems] Обрабатываем ${daysToProcess} дней`)
  
  // Конечная дата (последний элемент массива) - это endSec (сегодня)
  const endDate = new Date(endSec * 1000)
  const endDay = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate())
  
  const items: RangeChartItem[] = []
  
  // Строим даты НАЗАД от конечной даты
  // Последний элемент массива API (index = daysToProcess - 1) = endDay (сегодня)
  // Первый элемент массива API (index = 0) = endDay - (daysToProcess - 1) дней
  for (let i = 0; i < daysToProcess; i++) {
    const d = new Date(endDay)
    d.setDate(endDay.getDate() - (daysToProcess - 1 - i))
    const yyyy = d.getFullYear()
    const mm = String(d.getMonth() + 1).padStart(2, "0")
    const dd = String(d.getDate()).padStart(2, "0")
    items.push({ 
      date: `${yyyy}-${mm}-${dd}`, 
      value: metricCounts?.[i] ?? 0
    })
  }
  
  debugLog('api', `[buildChartItems] Первая дата: ${items[0]?.date}, последняя дата: ${items[items.length - 1]?.date}`)
  
  return items
}

function formatRuFullDate(iso: string): string {
  const [yyyy, mm, dd] = iso.split("-")
  const date = new Date(Number(yyyy), Number(mm) - 1, Number(dd))
  const str = new Intl.DateTimeFormat("ru-RU", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date)
  const cleaned = str.replace(" г.", "").replace(" г", "")
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1)
}
